import { useMemo, useState, useRef, type ReactNode, type KeyboardEvent } from 'react'
import { cx } from './cx'
import s from './TreeView.module.css'

export interface TreeNode {
  key: string
  label: ReactNode
  /** right-aligned slot — a count, a Pill, a privilege badge */
  meta?: ReactNode
  /** one-line caption under the label */
  subtitle?: ReactNode
  children?: TreeNode[]
  disabled?: boolean
}

export interface TreeViewProps {
  nodes: TreeNode[]
  /** controlled selection; omit for uncontrolled */
  selected?: string | null
  onSelect?: (key: string, node: TreeNode) => void
  /** controlled expansion; omit for uncontrolled */
  expanded?: string[]
  onToggle?: (key: string, next: boolean) => void
  defaultExpanded?: string[]
  /** expand everything on first render — for small trees where collapsing hides the point */
  defaultExpandAll?: boolean
  emptyLabel?: string
  className?: string
  testid?: string
}

/** Depth-first walk of the nodes currently VISIBLE (parents expanded). */
function visibleRows(nodes: TreeNode[], isOpen: (k: string) => boolean, level = 1):
{ node: TreeNode; level: number }[] {
  const out: { node: TreeNode; level: number }[] = []
  for (const n of nodes) {
    out.push({ node: n, level })
    if (n.children?.length && isOpen(n.key)) out.push(...visibleRows(n.children, isOpen, level + 1))
  }
  return out
}

function allKeys(nodes: TreeNode[]): string[] {
  return nodes.flatMap((n) => [n.key, ...(n.children ? allKeys(n.children) : [])])
}

/**
 * A hierarchy (the Kendo TreeView of the kit) — team and business-unit trees,
 * org charts, any parent/child structure where the SHAPE is the information.
 *
 * Built to the ARIA tree pattern rather than a pile of nested lists, because a
 * security tree is exactly where keyboard and screen-reader users end up: one
 * tab stop for the whole tree, arrows to move, ← / → to collapse and expand,
 * Home/End to jump. Roving tabindex, so tabbing past the tree does not mean
 * tabbing through every node.
 *
 * Selection and expansion are independently controllable — a page often drives
 * selection from the URL while letting the tree own its own open/closed state.
 */
export function TreeView({
  nodes, selected, onSelect, expanded, onToggle, defaultExpanded, defaultExpandAll,
  emptyLabel = 'Nothing here yet.', className, testid,
}: TreeViewProps) {
  const [openInternal, setOpenInternal] = useState<Set<string>>(() => new Set(defaultExpanded ?? []))
  // `defaultExpandAll` cannot be resolved in a useState initializer: nodes almost
  // always arrive from a query, so on first render there are none and the initial
  // set would be empty forever. Instead the default is applied LAZILY — every
  // node counts as open until the user actually toggles something, at which point
  // the current effective state is frozen into openInternal and takes over.
  const [touched, setTouched] = useState(false)
  const [selInternal, setSelInternal] = useState<string | null>(null)
  const [focusKey, setFocusKey] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  const openControlled = expanded !== undefined
  const isOpen = (k: string) =>
    openControlled ? expanded!.includes(k)
      : defaultExpandAll && !touched ? true
      : openInternal.has(k)
  const sel = selected !== undefined ? selected : selInternal

  const rows = useMemo(() => visibleRows(nodes, isOpen),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodes, openControlled ? expanded?.join('|') : `${touched}:${[...openInternal].join('|')}`])

  const toggle = (k: string, next: boolean) => {
    onToggle?.(k, next)
    if (openControlled) return
    // First interaction under defaultExpandAll: freeze "everything open" into
    // real state before applying the change, so collapsing one node does not
    // collapse the entire tree.
    const base = defaultExpandAll && !touched ? new Set(allKeys(nodes)) : null
    setTouched(true)
    setOpenInternal((prev) => {
      const n = new Set(base ?? prev)
      if (next) n.add(k); else n.delete(k)
      return n
    })
  }

  const select = (n: TreeNode) => {
    if (n.disabled) return
    onSelect?.(n.key, n)
    if (selected === undefined) setSelInternal(n.key)
  }

  // The tree is ONE tab stop: the focused node carries tabindex 0, the rest -1.
  const active = focusKey ?? sel ?? rows[0]?.node.key ?? null
  const focusAt = (i: number) => {
    const next = rows[Math.max(0, Math.min(rows.length - 1, i))]
    if (!next) return
    setFocusKey(next.node.key)
    ;(ref.current?.querySelector(`[data-key="${CSS.escape(next.node.key)}"]`) as HTMLElement | null)?.focus()
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = rows.findIndex((r) => r.node.key === active)
    if (i < 0) return
    const row = rows[i]!
    const hasKids = !!row.node.children?.length
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); focusAt(i + 1); break
      case 'ArrowUp': e.preventDefault(); focusAt(i - 1); break
      case 'Home': e.preventDefault(); focusAt(0); break
      case 'End': e.preventDefault(); focusAt(rows.length - 1); break
      case 'ArrowRight':
        e.preventDefault()
        if (hasKids && !isOpen(row.node.key)) toggle(row.node.key, true)
        else if (hasKids) focusAt(i + 1)          // already open → step into the first child
        break
      case 'ArrowLeft':
        e.preventDefault()
        if (hasKids && isOpen(row.node.key)) { toggle(row.node.key, false); break }
        // otherwise walk up to the nearest shallower row — the parent
        for (let j = i - 1; j >= 0; j--) if (rows[j]!.level < row.level) { focusAt(j); break }
        break
      case 'Enter': case ' ':
        e.preventDefault(); select(row.node); break
    }
  }

  if (!nodes.length) return <div className={cx(s.empty, className)}>{emptyLabel}</div>

  return (
    <div ref={ref} role="tree" className={cx(s.tree, className)} onKeyDown={onKeyDown} data-testid={testid}>
      {rows.map(({ node, level }) => {
        const hasKids = !!node.children?.length
        const open = hasKids && isOpen(node.key)
        return (
          <div
            key={node.key}
            role="treeitem"
            data-key={node.key}
            data-testid={testid ? `${testid}-${node.key}` : undefined}
            aria-level={level}
            aria-expanded={hasKids ? open : undefined}
            aria-selected={sel === node.key}
            aria-disabled={node.disabled || undefined}
            tabIndex={active === node.key ? 0 : -1}
            onFocus={() => setFocusKey(node.key)}
            onClick={() => select(node)}
            className={cx(s.row, sel === node.key && s.on, node.disabled && s.disabled)}
            style={{ ['--level' as string]: level }}
          >
            <span
              className={cx(s.chev, !hasKids && s.leaf)}
              aria-hidden="true"
              onClick={(e) => { if (hasKids) { e.stopPropagation(); toggle(node.key, !open) } }}
            >
              {hasKids ? '▸' : '·'}
            </span>
            <span className={s.labelWrap}>
              <span className={s.label}>{node.label}</span>
              {node.subtitle && <span className={s.subtitle}>{node.subtitle}</span>}
            </span>
            {node.meta != null && <span className={s.meta}>{node.meta}</span>}
          </div>
        )
      })}
    </div>
  )
}
